import React from 'react'
import GuideLayout from '@/components/help/GuideLayout'
import GuideSection from '@/components/help/GuideSection'
import GuideWidget from '@/components/help/GuideWidget'
import GuideTip from '@/components/help/GuideTip'
import GuideRelatedLinks from '@/components/help/GuideRelatedLinks'
import RateTimelineWidget from '@/components/help/widgets/RateTimelineWidget'
import type { SharedProps } from '@/types/shared'

// THE HISTORY GUIDE (docs/plans/2026-10-20-change-safety.md §10): both tabs, what a Changes entry
// shows, Undo and its conflicts, and why a link to prices never claims a cause. Served at
// /guides/history, and at /guides/change-log for links made before the page was renamed.
export default function HistoryGuide({ currentUser, properties }: Pick<SharedProps, 'currentUser' | 'properties'>) {
  return (
    <GuideLayout
      currentUser={currentUser}
      properties={properties}
      title="History"
      subtitle="Every price the engine moved, and every rule someone changed."
    >
      <GuideSection title="Why History Matters">
        <p className="text-sm leading-relaxed text-muted-foreground">
          A guest questions a rate they were quoted. A colleague asks why prices dropped on Tuesday.
          A base rate is suddenly half what it was and you need to know who changed it, and when.
          History answers all of these, for each hotel, on one page with two tabs.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Open <strong className="text-foreground">History</strong> in the sidebar. The{' '}
          <strong className="text-foreground">Prices</strong> tab lists every price the engine
          moved. The <strong className="text-foreground">Changes</strong> tab lists every change
          someone made to the hotel&rsquo;s pricing rules.
        </p>
      </GuideSection>

      <GuideSection title="The Prices Tab">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Every price the engine moved, newest first. Each row shows:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            The <strong className="text-foreground">room, rate plan and stay date</strong> whose
            price moved
          </li>
          <li>
            The <strong className="text-foreground">old and new price</strong>, green for a rise and
            red for a drop
          </li>
          <li>
            <strong className="text-foreground">What moved it</strong>: an ordinary update (Auto), a
            manual price (Manual override, with the person who set it), Last-Minute Clearance, or
            clearance ending, plus the parts of the engine that changed the price
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Filter by room, rate plan, what moved the price, or stay dates. The Prices tab keeps the
          last 90 days.
        </p>
      </GuideSection>

      <GuideWidget
        title="Rate History Example"
        footnote="Shows how a rate evolves over several days through recomputations and overrides."
      >
        <RateTimelineWidget />
      </GuideWidget>

      <GuideSection title="The Changes Tab">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Every change to this hotel&rsquo;s pricing: Rooms setup, day of week, demand response, gap
          filling, price guardrails, market position, clearance, seasons, events, overrides,
          targets and the rest. Changes are grouped by day, newest first, and each one shows:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Who made it</strong>, by the email they used at the
            time. A change made by our support team carries a Support badge. Changes nobody made by
            hand say so: Your PMS for a change that came from your PMS, Automatic for any other.
          </li>
          <li>
            <strong className="text-foreground">When</strong>, in the hotel&rsquo;s own time.
          </li>
          <li>
            <strong className="text-foreground">What changed</strong>, in one line. Open the entry to
            see each value as it was before and after, and what it applies to.
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Filter by person, area or dates. Changes are kept for good, so you can always look back. In
          a combined inventory, the lead listing&rsquo;s History covers every listing, and a change
          made on a following listing names it.
        </p>
      </GuideSection>

      <GuideSection title="Price Changes Since Then">
        <p className="text-sm leading-relaxed text-muted-foreground">
          An entry links to the Prices tab, filtered to the rooms it touched, from the minute it was
          saved. It shows what the engine did from then on. It does not say this change moved those
          prices: one update can apply several changes at once, and bookings move prices too. When
          the change is older than the Prices tab keeps, there is no link.
        </p>
      </GuideSection>

      <GuideSection title="Undo">
        <p className="text-sm leading-relaxed text-muted-foreground">
          A change to Rooms setup, day of week, demand response, gap filling, price guardrails,
          market position or clearance settings can be undone from its entry by anyone who can change
          pricing at the hotel.
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            Undo puts back the values that entry changed, those and no others, and shows you the
            review before anything is saved, like any other pricing save.
          </li>
          <li>
            If someone changed one of those values since, the review lists it, with its value now and
            who changed it, and asks you to confirm before overwriting it.
          </li>
          <li>
            The undo is recorded as a new entry, and the original shows who undid it and when. The
            engine then recomputes prices from the restored rules, as it does after any rule change.
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Seasons, events, overrides, combined inventory, onboarding, and changes made by support, by
          your PMS or automatically cannot be undone from History. Change those on their own page.
        </p>
      </GuideSection>

      <GuideSection title="Who Can See It">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Everyone who can open a hotel can read its History, viewers included. Owners and admins
          also have <strong className="text-foreground">Activity</strong> in Business settings:
          every hotel&rsquo;s pricing changes in one list, alongside changes to the team, billing and
          the business.
        </p>
      </GuideSection>

      <div className="space-y-3">
        <GuideTip type="do">
          When a price looks wrong, open the Changes tab first and filter by area: the newest entry
          tells you who last touched that rule, and when.
        </GuideTip>
        <GuideTip type="dont">
          Don&rsquo;t read a price move on the Prices tab as the result of one change. Several changes
          and new bookings can land in the same update.
        </GuideTip>
      </div>

      <GuideRelatedLinks
        links={[
          { slug: 'rate-calendar', title: 'Rate Calendar' },
          { slug: 'overrides', title: 'Overrides' },
          { slug: 'how-it-works', title: 'How It Works' },
        ]}
      />
    </GuideLayout>
  )
}
